import React, { useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import isEqual from 'lodash/isEqual';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { CardClient, SmartCardProvider } from '@atlaskit/link-provider';
import { ReactRenderer } from '@atlaskit/renderer';
import { FireUIAnalytics } from '@atlassian/analytics-bridge';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { ExperienceSuccess } from '@atlassian/ufo';
import { PackageIntlProvider, withPackageIntlProvider } from '../../intl-provider';
import { useMediaContext } from '../../jira-common/context';
import { useEmitAttachmentMetrics } from '../../jira-common/utils/analytics/attachment';
import { HtmlExtension } from './extensions/HtmlExtension';
import { rendererEventHandlers } from './rendererEventHandlers';
import { RenderQuestion } from './RenderQuestion';
import { IntlViewFormMessages, ViewFormMessage } from './ViewFormMessages.intl';

/**
 * Ths is hardcoded as 'light' everywhere except for `template-preview`.
 * See `/jira/src/packages/proforma/proforma-template-library/src/ui/library/template-preview/index.tsx`.
 */

export let DisplayMode = /*#__PURE__*/function (DisplayMode) {
  /**
   * View: Display the form as a plain document, display all answers are plain text
   */
  DisplayMode["View"] = "view";
  /**
   * Edit: Display the form in edit mode with all input fields
   */
  DisplayMode["Edit"] = "edit";
  /**
   * Preview: Display the form with all input fields disabled
   */
  DisplayMode["Preview"] = "preview";
  /**
   * Translate: Display the form for translating with all input fields disabled
   */
  DisplayMode["Translate"] = "translate";
  return DisplayMode;
}({});
const environment = window.location.hostname === 'localhost' ? 'staging' : undefined; // Make smart links connect to staging when developing locally.
const smartCardClient = new CardClient(environment);
const formExtensionFn = (intl, formStoreRef, displayModeRef, blockFormSubmission, linkedFieldNames, QuestionRenderer, questionTypeRendererMap
// eslint-disable-next-line @typescript-eslint/ban-types
) => {
  return ext => {
    const formStore = formStoreRef.current;
    if (ext.extensionKey === 'question') {
      var _ext$parameters, _ref, _ref2, _formStore$translated;
      const questionId = (_ext$parameters = ext.parameters) === null || _ext$parameters === void 0 ? void 0 : _ext$parameters.id;
      const questionStore = questionId !== undefined && formStore.questions.find(question => question.id === questionId);
      if (!questionStore) {
        // eslint-disable-next-line no-console
        console.error(`Could not find the question store with ID ${questionId}:`, ext);
        return /*#__PURE__*/React.createElement(React.Fragment, null);
      }
      const displayLocale = (_ref = (_ref2 = (_formStore$translated = formStore.translatedLocale) !== null && _formStore$translated !== void 0 ? _formStore$translated : formStore.primaryLocale) !== null && _ref2 !== void 0 ? _ref2 : formStore.language) !== null && _ref !== void 0 ? _ref : intl.locale;
      return /*#__PURE__*/React.createElement(PackageIntlProvider, {
        locale: displayLocale
      }, /*#__PURE__*/React.createElement(RenderQuestion, {
        questionStore: questionStore,
        displayMode: displayModeRef.current,
        formStore: formStore,
        blockFormSubmission: blockFormSubmission,
        linkedFieldNames: linkedFieldNames,
        QuestionRenderer: QuestionRenderer,
        questionTypeRendererMap: questionTypeRendererMap
      }));
    }
    if (ext.extensionKey === 'html') {
      return /*#__PURE__*/React.createElement(HtmlExtension, {
        extensionId: ext.parameters.id,
        htmlContent: ext.parameters.htmlContent || ''
      });
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  };
};
export const isSummaryOnTopLevel = (linkedSummaryQuestionId, formLayout) => {
  var _formLayout$some;
  return (_formLayout$some = formLayout === null || formLayout === void 0 ? void 0 : formLayout.some(layout => layout.content.some(content => {
    var _content$attrs, _content$attrs$parame;
    return content.type === 'extension' && ((_content$attrs = content.attrs) === null || _content$attrs === void 0 ? void 0 : (_content$attrs$parame = _content$attrs.parameters) === null || _content$attrs$parame === void 0 ? void 0 : _content$attrs$parame.id) === linkedSummaryQuestionId;
  }))) !== null && _formLayout$some !== void 0 ? _formLayout$some : false;
};
export const RenderForm = withPackageIntlProvider(observer(({
  formStore,
  showHiddenSections,
  displayMode,
  backgroundColor,
  ufoExperience,
  blockFormSubmission,
  linkedFieldNames,
  QuestionRenderer,
  questionTypeRendererMap,
  nodeComponents
}) => {
  var _formStore$layout, _ref3, _ref4, _formStore$translated2, _formStore$language;
  const intl = useIntl();
  const formStoreRef = useRef(formStore);
  if (formStoreRef.current !== formStore) {
    formStoreRef.current = formStore;
  }
  const displayModeRef = useRef(displayMode);
  if (displayModeRef.current !== displayMode) {
    displayModeRef.current = displayMode;
  }
  const {
    mediaReadTokenRefresh
  } = useMediaContext();
  const [attachmentIdsCache, setAttachmentIdsCache] = useState(new Set());
  useEffect(() => {
    let mediaTokenRefreshTimeoutId;
    if (!isEqual(attachmentIdsCache, formStore.attachmentIds)) {
      setAttachmentIdsCache(new Set([...formStore.attachmentIds]));
      if (formStore.attachmentIds.size > 0 && mediaReadTokenRefresh) {
        mediaTokenRefreshTimeoutId = setTimeout(() => mediaReadTokenRefresh(), 1000);
      }
    }
    return () => {
      if (mediaTokenRefreshTimeoutId) {
        clearInterval(mediaTokenRefreshTimeoutId);
      }
    };
    // removing attachmentIdsCache from dependency array as we set it in the useEffect
    // this will immediately trigger the useEffect again which calls the returned cleanup function
    // this will clear the timeout which means the token won't be refreshed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStore.attachmentIds, mediaReadTokenRefresh]);
  useEmitAttachmentMetrics(formStore, displayMode !== DisplayMode.Edit);
  useEffect(() => {
    blockFormSubmission && blockFormSubmission(formStore.isUploadingMedia);
  }, [blockFormSubmission, formStore.isUploadingMedia]);
  const formSections = (_formStore$layout = formStore.layout) === null || _formStore$layout === void 0 ? void 0 : _formStore$layout.map((sectionLayout, index) => {
    const sectionId = index.toString();
    if (showHiddenSections === undefined && !formStore.visibleSections.includes(sectionId)) {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
    const formSection = /*#__PURE__*/React.createElement(ReactRenderer, {
      key: sectionId,
      document: toJS(sectionLayout),
      extensionHandlers: {
        'com.thinktilt.proforma': formExtensionFn(intl, formStoreRef, displayModeRef, blockFormSubmission, linkedFieldNames, QuestionRenderer, questionTypeRendererMap)
      },
      eventHandlers: rendererEventHandlers,
      nodeComponents: nodeComponents,
      adfStage: "stage0"
    });
    if (showHiddenSections && !formStore.visibleSections.includes(sectionId)) {
      return /*#__PURE__*/React.createElement(SectionWrapperStyles, {
        key: sectionId
      }, /*#__PURE__*/React.createElement("div", null, formSection), /*#__PURE__*/React.createElement(SectionOverlayStyles, {
        hiddenSectionTheme: showHiddenSections
      }, /*#__PURE__*/React.createElement(SectionMessageStyles, {
        hiddenSectionTheme: showHiddenSections
      }, /*#__PURE__*/React.createElement(FormattedMessage, IntlViewFormMessages[ViewFormMessage.HiddenSection]))));
    }
    return formSection;
  });
  const linkedSummaryQuestionId = formStore === null || formStore === void 0 ? void 0 : formStore.questions.find(question => question.formQuestion.jiraField === 'summary');
  const displayLocale = (_ref3 = (_ref4 = (_formStore$translated2 = formStore.translatedLocale) !== null && _formStore$translated2 !== void 0 ? _formStore$translated2 : formStore.primaryLocale) !== null && _ref4 !== void 0 ? _ref4 : formStore.language) !== null && _ref3 !== void 0 ? _ref3 : intl.locale;
  const form = /*#__PURE__*/React.createElement(FormContainer, null, /*#__PURE__*/React.createElement(FormStyles, {
    backgroundColor: backgroundColor
  }, /*#__PURE__*/React.createElement(SmartCardProvider, {
    client: smartCardClient
  }, formSections)), !!ufoExperience && /*#__PURE__*/React.createElement(ExperienceSuccess, {
    experience: ufoExperience
  }), /*#__PURE__*/React.createElement(FireUIAnalytics, {
    actionSubject: ActionSubject.FORM,
    action: Action.VIEWED,
    actionSubjectId: "proformaRenderForm",
    attributes: {
      ...formStore.analyticsAttributes,
      hasLinkedSummary: !!linkedSummaryQuestionId,
      summaryOnTopLevel: linkedSummaryQuestionId ? isSummaryOnTopLevel(linkedSummaryQuestionId.id, formStore === null || formStore === void 0 ? void 0 : formStore.layout) : false,
      translationDetails: {
        legacyLanguage: (_formStore$language = formStore.language) === null || _formStore$language === void 0 ? void 0 : _formStore$language.replace('_', '-'),
        localeMatched: displayLocale === intl.locale,
        primaryLocale: formStore.primaryLocale,
        translated: formStore.translatedLocale !== formStore.primaryLocale,
        translatedLocale: formStore.translatedLocale,
        translationsApplied: formStore.translatedLocale !== undefined,
        userLocale: intl.locale
      }
    }
  }));
  if (displayLocale !== undefined) {
    return /*#__PURE__*/React.createElement(PackageIntlProvider, {
      locale: displayLocale
    }, form);
  }
  return form;
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormContainer = styled.div({
  height: 'auto',
  overflow: 'visible'
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const FormStyles = styled.div(({
  backgroundColor
}) => ({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : "var(--ds-surface, white)",
  padding: "var(--ds-space-100, 8px)",
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '& .ak-renderer-extension-overflow-container': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
    overflow: 'unset !important'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '& .pm-table-container': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
    zIndex: 'auto !important'
  }
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionWrapperStyles = styled.div({
  position: 'relative',
  padding: "var(--ds-space-050, 4px)"
});

/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-dynamic-styles */
/* eslint-disable @atlaskit/ui-styling-standard/no-styled */
const SectionOverlayStyles = styled.div(({
  hiddenSectionTheme
}) => ({
  height: '100%',
  width: '100%',
  backgroundColor: hiddenSectionTheme === 'dark' ? "var(--ds-background-disabled, rgba(108, 121, 143, 0.1))" : undefined,
  top: 0,
  left: 0,
  position: 'absolute',
  borderRadius: "var(--ds-border-radius, 3px)",
  textAlign: 'center',
  pointerEvents: 'none'
}));
const SectionMessageStyles = styled.h4(({
  hiddenSectionTheme
}) => ({
  padding: "var(--ds-space-100, 8px)",
  color: hiddenSectionTheme === 'light' ? "var(--ds-text, #091E42)" : "var(--ds-text, #172b4d)",
  backgroundColor: hiddenSectionTheme === 'light' ? "var(--ds-background-accent-gray-subtler, #DFE1E6)" : "var(--ds-surface, white)",
  display: 'inline-block',
  position: 'relative',
  top: "var(--ds-space-100, 8px)",
  borderRadius: "var(--ds-border-radius-200, 8px)"
}));
/* eslint-enable @atlaskit/ui-styling-standard/no-unsafe-values */
/* eslint-enable @atlaskit/ui-styling-standard/no-dynamic-styles */
/* eslint-enable @atlaskit/ui-styling-standard/no-styled */